import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
// import { motion } from 'framer-motion';
import { 
  FiSave, 
  FiTrash2, 
  FiArrowLeft, 
  FiMapPin, 
  FiX,
  FiCheck,
  FiAlertCircle
} from 'react-icons/fi';
import NoteEditor from '../../components/notes/NoteEditor';
import CourseSelect from '../../components/notes/CourseSelect';
import WeekSelect from '../../components/notes/WeekSelect';
import MyNotesSidebar from '../../components/notes/MyNotesSidebar';
import notesApi from '../../services/notesApi';

const PageContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  height: calc(100vh - 2rem);
  max-height: calc(100vh - 2rem);
  overflow: hidden;
  padding: 0 1rem;
  
  @media (min-width: 768px) {
    padding: 0 1.5rem;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: 320px 1fr;
    padding: 0 2rem;
  }
  
  @media (min-width: 1280px) {
    padding: 0 2.5rem;
  }
  
  @media (max-height: 600px) {
    height: calc(100vh - 1rem);
    max-height: calc(100vh - 1rem);
    gap: 0.5rem;
  }
  
  @media (max-width: 640px) {
    padding: 0 0.5rem;
    gap: 0.5rem;
  }
`;

const EditorContainer = styled.div`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 20px;
    gap: 16px;
  }
  
  @media (max-width: 640px) {
    padding: 16px;
    gap: 12px;
    border-radius: 0;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    gap: 10px;
  }
  
  @media (max-height: 600px) {
    padding: 16px;
    gap: 12px;
    border-radius: 0;
  }
  
  @media (max-height: 500px) {
    padding: 12px;
    gap: 8px;
  }
`;

const EditorHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    gap: 12px;
    padding-bottom: 12px;
  }
  
  @media (max-width: 640px) {
    gap: 10px;
    padding-bottom: 10px;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
    padding-bottom: 8px;
  }
  
  @media (max-height: 600px) {
    gap: 12px;
    padding-bottom: 12px;
  }
  
  @media (max-height: 500px) {
    gap: 8px;
    padding-bottom: 8px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  
  &:hover {
    background: var(--background-tertiary);
    border-color: var(--border-dark);
  }
`;

const TitleInput = styled.input`
  flex: 1;
  min-width: 200px;
  padding: 12px 16px;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 16px;
  font-weight: 500;
  color: var(--text-primary);
  background: var(--background-primary);
  height: 48px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
  
  @media (max-height: 600px) {
    height: 40px;
    padding: 8px 12px;
    font-size: 14px;
  }
  
  @media (max-height: 500px) {
    height: 36px;
    padding: 6px 10px;
    font-size: 14px;
  }
`;

const ControlsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

const SaveStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: ${props => {
    if (props.status === 'saving') return 'var(--warning-color)';
    if (props.status === 'saved') return 'var(--success-color)';
    if (props.status === 'error') return 'var(--error-color)';
    return 'var(--text-muted)';
  }};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background: var(--background-primary);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  
  &:hover {
    background: var(--background-secondary);
    border-color: var(--border-dark);
  }
  
  &.primary {
    background: var(--primary-color);
    color: var(--text-inverse);
    border-color: var(--primary-color);
    
    &:hover {
      background: var(--primary-dark);
      border-color: var(--primary-dark);
    }
  }
  
  &.danger {
    color: var(--error-color);
    border-color: var(--error-color);
    
    &:hover {
      background: rgba(252, 129, 129, 0.1);
      border-color: var(--error-dark);
    }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EditorContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: 100%;
  overflow: hidden;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid var(--border-color);
  flex-shrink: 0;
  
  @media (max-height: 600px) {
    padding-top: 12px;
    gap: 8px;
  }
  
  @media (max-height: 500px) {
    padding-top: 8px;
    gap: 6px;
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(252, 129, 129, 0.1);
  border: 1px solid var(--error-color);
  border-radius: 0;
  color: var(--error-color);
  font-size: 14px;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--background-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

// eslint-disable-next-line import/first
// (All imports must be at top; removing body import)

export default function NoteEditorPage() {
  const { noteId } = useParams();
  const navigate = useNavigate();
  const [note, setNote] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const autosaveTimeoutRef = useRef(null);
  const lastSavedContentRef = useRef(null);

  const isNewNote = !noteId || noteId === 'new';

  // Fetch note data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch courses
        const coursesResponse = await notesApi.getCourses();
        if (coursesResponse.success) {
          setCourses(coursesResponse.courses || []);
        }

        // Fetch note only when we have a valid id and it's not a new note
        if (noteId && !isNewNote) {
          const noteResponse = await notesApi.getNote(noteId);
          if (noteResponse.success) {
            setNote(noteResponse.note);
            lastSavedContentRef.current = JSON.stringify(noteResponse.note);
          } else {
            setError('Note not found');
          }
        } else {
          // Initialize new note
          setNote({
            title: '',
            course_id: null,
            week_number: null,
            content_json: { type: 'doc', content: [] },
            content_html: '',
            content_plain: '',
            is_pinned: false
          });
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load note');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [noteId, isNewNote]);

  // Autosave functionality
  const autosave = useCallback(async (contentData) => {
    if (!note || isNewNote) return;

    try {
      setSaving(true);
      setSaveStatus('saving');
      
      await notesApi.autosaveNote(note.id, contentData);
      
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      console.error('Autosave error:', err);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  }, [note, isNewNote]);

  // Handle content changes
  const handleContentChange = useCallback((contentData) => {
    setNote(prev => ({ ...prev, ...contentData }));
    
    // Clear existing timeout
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }
    
    // Set new autosave timeout (5 seconds)
    autosaveTimeoutRef.current = setTimeout(() => {
      autosave(contentData);
    }, 5000);
  }, [autosave]);

  // Handle field changes
  const handleFieldChange = useCallback((field, value) => {
    setNote(prev => ({ ...prev, [field]: value }));
  }, []);

  // Save note
  const saveNote = useCallback(async () => {
    if (!note) return;

    try {
      // Basic client-side validation for required fields when creating
      if (isNewNote) {
        const missing = [];
        if (!note.title || !note.title.trim()) missing.push('title');
        if (!note.course_id) missing.push('course');
        if (!note.week_number) missing.push('week');
        if (missing.length > 0) {
          setError(`Please provide ${missing.join(', ')}`);
          setSaveStatus('error');
          return;
        }
      }

      setSaving(true);
      setSaveStatus('saving');

      if (isNewNote) {
        // Create new note
        const response = await notesApi.createNote(note);
        if (response.success) {
          navigate(`/student/notes/${response.note.id}`, { replace: true });
          setNote(response.note);
          lastSavedContentRef.current = JSON.stringify(response.note);
        } else {
          setError(response.message || 'Failed to create note');
          setSaveStatus('error');
          return;
        }
      } else {
        // Update existing note
        const response = await notesApi.updateNote(note.id, note);
        if (response.success) {
          setNote(response.note);
          lastSavedContentRef.current = JSON.stringify(response.note);
        } else {
          setError(response.message || 'Failed to save note');
          setSaveStatus('error');
          return;
        }
      }
      
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      console.error('Save error:', err);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  }, [note, isNewNote, navigate]);

  // Delete note
  const deleteNote = async () => {
    if (!note || isNewNote) return;
    
    if (window.confirm('Are you sure you want to delete this note? This action cannot be undone.')) {
      try {
        await notesApi.deleteNote(note.id);
        navigate('/student/notes');
      } catch (err) {
        console.error('Delete error:', err);
        setError('Failed to delete note');
      }
    }
  };

  // Toggle pin
  const togglePin = async () => {
    if (!note || isNewNote) return;

    try {
      const response = note.is_pinned 
        ? await notesApi.unpinNote(note.id)
        : await notesApi.pinNote(note.id);
      
      if (response.success) {
        setNote(prev => ({ ...prev, is_pinned: response.is_pinned }));
      }
    } catch (err) {
      console.error('Pin toggle error:', err);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        saveNote();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [saveNote]);

  // Cleanup autosave timeout
  useEffect(() => {
    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <LoadingOverlay>
        <div>Loading note...</div>
      </LoadingOverlay>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <EditorContainer>
          <ErrorMessage>
            <FiAlertCircle size={16} />
            {error}
          </ErrorMessage>
          <BackButton onClick={() => navigate('/student/notes')}>
            <FiArrowLeft size={16} />
            Back to Notes
          </BackButton>
        </EditorContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <MyNotesSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        currentNoteId={note?.id}
      />
      
      <EditorContainer>
        <EditorHeader>
          <BackButton onClick={() => navigate('/student/notes')}>
            <FiArrowLeft size={16} />
            Back
          </BackButton>
          
          <TitleInput
            type="text"
            placeholder="Note title..."
            value={note?.title || ''}
            onChange={(e) => handleFieldChange('title', e.target.value)}
          />
          
          <ControlsRow>
            <CourseSelect
              value={note?.course_id}
              onChange={(value) => handleFieldChange('course_id', value)}
              courses={courses}
            />
            
            <WeekSelect
              value={note?.week_number}
              onChange={(value) => handleFieldChange('week_number', value)}
            />
            
            <SaveStatus status={saveStatus}>
              {saveStatus === 'saving' && <FiSave size={14} />}
              {saveStatus === 'saved' && <FiCheck size={14} />}
              {saveStatus === 'error' && <FiAlertCircle size={14} />}
              {saveStatus === 'saving' && 'Saving...'}
              {saveStatus === 'saved' && 'Saved'}
              {saveStatus === 'error' && 'Error'}
              {saveStatus === 'idle' && 'All changes saved'}
            </SaveStatus>
          </ControlsRow>
        </EditorHeader>

        <EditorContent>
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <NoteEditor
              note={note}
              onChange={handleContentChange}
              placeholder="Start writing your note..."
            />
          </div>
        </EditorContent>

        <ActionButtonsContainer>
          {!isNewNote && (
            <>
              <ActionButton onClick={togglePin}>
                {note?.is_pinned ? <FiX size={16} /> : <FiMapPin size={16} />}
                {note?.is_pinned ? 'Unpin' : 'Pin'}
              </ActionButton>
              
              <ActionButton onClick={deleteNote} className="danger">
                <FiTrash2 size={16} />
                Delete
              </ActionButton>
            </>
          )}
          
          <ActionButton 
            onClick={saveNote} 
            className="primary"
            disabled={saving}
          >
            <FiSave size={16} />
            {isNewNote ? 'Create Note' : 'Save Changes'}
          </ActionButton>
        </ActionButtonsContainer>
      </EditorContainer>
    </PageContainer>
  );
}
